import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Wrench, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/api';

/** One-click cleanup of stale search-history rows (legacy $0.00 symbols). */
const MaintenanceCard = () => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const data = await adminAPI.cleanupSearchHistory(dryRun);
      setResult(data);
      if (dryRun) {
        toast.info(
          data.stale_symbols.length
            ? `${data.stale_symbols.length} stale symbols found (${data.affected_rows} rows)`
            : 'No stale search-history rows found',
        );
      } else {
        toast.success(`Deleted ${data.deleted_rows} stale rows`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700" data-testid="admin-maintenance-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Wrench className="w-5 h-5 text-cyan-400" /> Maintenance
        </CardTitle>
        <CardDescription className="text-gray-400">
          Remove search-history rows whose symbols no longer resolve to a valid quote
          (legacy $0.00 entries). Preview first, then delete.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => run(true)}
            className="border-gray-600 text-gray-200 hover:bg-gray-700"
            data-testid="maintenance-dryrun-btn"
          >
            <Search size={14} className="mr-1" /> {busy ? 'Scanning…' : 'Preview (dry run)'}
          </Button>
          <Button
            size="sm"
            disabled={busy || !result || result.stale_symbols.length === 0}
            onClick={() => run(false)}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="maintenance-delete-btn"
          >
            <Trash2 size={14} className="mr-1" /> Delete stale rows
          </Button>
        </div>
        {result && (
          <p className="text-xs text-gray-400" data-testid="maintenance-result">
            Checked {result.symbols_checked} symbols ·{' '}
            {result.stale_symbols.length > 0
              ? `stale: ${result.stale_symbols.join(', ')} (${result.affected_rows} rows${result.dry_run ? '' : `, ${result.deleted_rows} deleted`})`
              : 'no stale symbols 🎉'}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default MaintenanceCard;
